// frontend/src/components/ExpenseManagement/ExpenseList.js
import React, { useMemo, useState } from 'react';
import ExpenseItem from './ExpenseItem';
import { Loader2, Filter, ChevronDown, ChevronUp, Search, X } from 'lucide-react';

const ExpenseList = ({
  expenses,
  loading,
  projects,
  onDelete,
  onUpdate,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  filters,
  onFilterChange,
  isAdmin,
  currentUserId,
  apiBaseUrl // S'assurer que cette prop est reçue de ExpenseManagement
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const totalPages = Math.ceil(totalItems / pageSize);
  const trimmedQuery = searchQuery.trim().toLowerCase();
  const filteredExpenses = useMemo(() => {
    if (!trimmedQuery) {
      return expenses;
    }
    return expenses.filter((expense) => {
      const haystack = [
        expense.description,
        expense.vendor,
        expense.employee_name,
        expense.created_by_user_name,
        ...(expense.project_allocations || []).map((allocation) => allocation.project_name),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(trimmedQuery);
    });
  }, [expenses, trimmedQuery]);
  const hasSearch = trimmedQuery.length > 0;

  return (
    <div className="mt-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full sm:max-w-md">
          <label htmlFor="expense-search-input" className="sr-only">Rechercher une dépense</label>
          <div className="flex items-center gap-2 border border-gray-300 rounded-md bg-white px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-indigo-100 focus-within:border-indigo-400">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              id="expense-search-input"
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Rechercher (description, fournisseur, projet...)"
              className="flex-1 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none"
            />
            {hasSearch && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label="Effacer la recherche"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm"
          >
            <Filter className="w-4 h-4 mr-1.5" />
            Filtres {showFilters ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Projet</label>
              <select
                name="project_id"
                value={filters.project_id || ''}
                onChange={(e) => onFilterChange({ ...filters, project_id: e.target.value === '' ? null : e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md bg-white text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Tous les projets</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
            {isAdmin && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Employé (ID)</label>
                <input
                  type="number"
                  name="employee_id"
                  value={filters.employee_id || ''}
                  onChange={(e) => onFilterChange({ ...filters, employee_id: e.target.value === '' ? null : e.target.value })}
                  placeholder="ID employé"
                  className="w-full p-2 border border-gray-300 rounded-md text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            )}
          </div>
          <div className="mt-3 flex justify-end">
            <button onClick={() => onFilterChange({ employee_id: null, project_id: null })} className="text-xs text-gray-500 hover:text-gray-700 hover:underline">
              Réinitialiser
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="p-10 text-center text-gray-500 flex items-center justify-center">
          <Loader2 className="animate-spin h-6 w-6 mr-2" /> Chargement des dépenses...
        </div>
      )}

      {!loading && filteredExpenses.length === 0 && (
        <div className="p-10 text-center text-gray-500 italic border-dashed border-2 border-gray-200 rounded-lg">
          {hasSearch ? 'Aucune dépense ne correspond à votre recherche.' : 'Aucune dépense à afficher pour les filtres sélectionnés.'}
        </div>
      )}

      {!loading && filteredExpenses.length > 0 && (
        <div className="space-y-3">
          {filteredExpenses.map(expense => (
            <ExpenseItem
              key={expense.id}
              expense={expense}
              projects={projects}
              onDelete={onDelete}
              onUpdate={onUpdate}
              isAdmin={isAdmin}
              currentUserId={currentUserId}
              apiBaseUrl={apiBaseUrl} // << PASSER LA PROP ICI
            />
          ))}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Précédent
            </button>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Affichage de <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> à <span className="font-medium">{Math.min(currentPage * pageSize, totalItems)}</span> sur <span className="font-medium">{totalItems}</span> résultats
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">Précédent</span>
                  <ChevronDown className="h-5 w-5 transform rotate-90" aria-hidden="true" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1 || (currentPage <= 3 && page <= 3) || (currentPage >= totalPages - 2 && page >= totalPages - 2))
                  .map((page, idx, arr) => (
                    <React.Fragment key={page}>
                      {idx > 0 && arr[idx - 1] < page - 1 && (
                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>
                      )}
                      <button
                        onClick={() => onPageChange(page)}
                        aria-current={page === currentPage ? 'page' : undefined}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === currentPage ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))
                }
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">Suivant</span>
                  <ChevronDown className="h-5 w-5 transform -rotate-90" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseList;
